'use client'

// import { useRect } from 'hamo'
import { useLenis } from 'lenis/react'
import { usePathname, useRouter } from 'next/navigation'
import {
  type PropsWithChildren,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDeviceDetection } from '~/hooks/use-device-detection'
import {
  defaultGlobalPageTransitionFns,
  defaultPageTransitionFns,
  PageTransitionContext,
} from './context'
import type { GlobalPageTransitionFns, PageTransitionFns } from './types'

export function PageTransitionProvider({ children }: PropsWithChildren) {
  // Global state
  const pathname = usePathname()
  const lenis = useLenis()
  const router = useRouter()
  const { isDesktop } = useDeviceDetection()

  // Local state
  const [transitionFns, setTransitionFns] = useState<PageTransitionFns>(
    defaultPageTransitionFns
  )
  const [globalTransitionFns, setGlobalTransitionFns] =
    useState<GlobalPageTransitionFns>(defaultGlobalPageTransitionFns)
  const [globalTransitionPages, setGlobalTransitionPages] = useState<string[]>(
    []
  )

  // Element refs
  const elementRefs = useRef<Record<string, RefObject<unknown>>>({})
  const globalElementRefs = useRef<Record<string, RefObject<unknown>>>({})

  // Compare refs
  const oldPathname = useRef(pathname)
  const lastEnterTransition = useRef(pathname + oldPathname.current)
  const oldLenis = useRef(lenis)

  // Exit transition
  const navigate = useCallback(
    async (href: string) => {
      if (href === pathname) return
      const info = {
        from: pathname,
        to: href,
        lenis,
        state: 'exiting' as const,
        device: isDesktop ? 'desktop' : 'mobile',
      } as const

      router.prefetch(href)
      const isGlobalTransitionPage = globalTransitionPages.includes(href)
      // Exit transition fns
      if (isGlobalTransitionPage) {
        await globalTransitionFns.onBeforeExit(
          elementRefs.current,
          globalElementRefs.current,
          info
        )
      }
      await transitionFns.exit(
        elementRefs.current,
        globalElementRefs.current,
        info
      )
      if (isGlobalTransitionPage) {
        await globalTransitionFns.onAfterExit(
          elementRefs.current,
          globalElementRefs.current,
          info
        )
      }

      // Update state
      oldPathname.current = pathname
      elementRefs.current = {}
      router.push(href)
    },
    [
      pathname,
      transitionFns,
      lenis,
      globalTransitionFns,
      router,
      isDesktop,
      globalTransitionPages,
    ]
  )

  // Enter transition
  useEffect(() => {
    if (!lenis || lenis === oldLenis.current) return
    oldLenis.current = lenis
    if (pathname === oldPathname.current) return
    if (lastEnterTransition.current === pathname + oldPathname.current) return
    lastEnterTransition.current = pathname + oldPathname.current

    const enter = async () => {
      const info = {
        from: oldPathname.current,
        to: pathname,
        lenis,
        state: 'entering' as const,
        device: isDesktop ? 'desktop' : 'mobile',
      } as const

      const isGlobalTransitionPage = globalTransitionPages.includes(pathname)
      if (isGlobalTransitionPage) {
        await globalTransitionFns.onBeforeEnter(
          elementRefs.current,
          globalElementRefs.current,
          info
        )
      }
      await transitionFns.enter(
        elementRefs.current,
        globalElementRefs.current,
        info
      )
      if (isGlobalTransitionPage) {
        void globalTransitionFns.onAfterEnter(
          elementRefs.current,
          globalElementRefs.current,
          info
        )
      }

      // useRect.resize()
    }

    enter()
  }, [
    pathname,
    transitionFns,
    lenis,
    globalTransitionFns,
    globalTransitionPages,
    isDesktop,
  ])

  // Context value
  const contextValue = useMemo(() => {
    return {
      transitionFns,
      setTransitionFns,
      globalTransitionFns,
      setGlobalTransitionFns,
      navigate,
      elementRefs,
      globalElementRefs,
      setGlobalTransitionPages,
    }
  }, [transitionFns, globalTransitionFns, navigate])

  // Provider
  return (
    <PageTransitionContext value={contextValue}>
      {children}
    </PageTransitionContext>
  )
}
